import React, { useMemo, useRef, useState } from "react"
import { Button, Col, Form, Input, Label, Row } from "reactstrap"

import "flatpickr/dist/themes/material_blue.css"
import Flatpickr from "react-flatpickr"
import moment from "moment"
import { useSelector, useDispatch } from "react-redux"
import { isObject } from "lodash"
import Select from "react-select"
import {
  getProviderItemCategories,
  getProviderItems,
} from "store/provider-dashboard/provider-dashboard-action"
import { providerDashboardAction } from "store/provider-dashboard/provider-dashboard-slice"
import ReportColumnSelector from "components/Common/ReportColumnSelector"

const ProviderReceiptReportFilter = ({
  onAdvancedSearchSubmit,
  loading,
  columns,
  onToggleColumn,
  onReorder = () => {},
}) => {
  const dateFlatpickerfrom = useRef(null)
  const dateFlatpickerto = useRef(null)
  const [category, setCategory] = useState(null)
  const [item, setItem] = useState(null)
  const [shipment, setShipment] = useState(null)
  const [fundCode, setFundCode] = useState(null)
  const [commodityClassification, setCommodityClassification] = useState(null)
  const [currency, setCurrency] = useState(null)
  const [batch_number, setBatchNumber] = useState("")
  const [receiptNumber, setReceiptNumber] = useState("")
  const [waybillNumber, setWaybillNumber] = useState("")

  const dispatch = useDispatch()

  const [date, setDate] = useState({
    from: null,
    to: null,
  })

  // Retrieve categories data from Redux store
  const categories = useSelector(
    state => state.providerDashboard.ProviderItemCategoires
  )
  // Retrieve items data from Redux store
  const items = useSelector(state => state.providerDashboard.ProviderItems)

  // Retrieve warehouses data from Redux store
  const warehouses = useSelector(
    state => state.providerDashboard.providerWarehouses
  )

  // Retrieve currencies data from Redux store
  const currencies = useSelector(
    state => state.providerDashboard.ProviderCurrencies
  )

  const shipments = useSelector(
    state => state.providerDashboard.ProviderShipments
  )
  const fundCodes = useSelector(
    state => state.providerDashboard.ProviderFundCodes
  )
  const commodityClassifications = useSelector(
    state => state.providerDashboard.ProviderCommodityClassifications
  )

  // get filtered items based select box props needs
  const filteredItems = useMemo(
    () =>
      items?.data.map(item => ({
        value: item.id,
        label: `${item.unfp_item_name} ( ${item.item_name} )`,
      })),
    [items]
  )

  // Store filtered data and dispatch the action using onAdvancedSearchSubmit callback function
  const handleSubmit = () => {
    let data = {}

    data.date = {
      from: date.from ? date.from : null,
      to: date.to ? date.to : null,
    }
    if (item) {
      data.item = item.map(ite => (ite.value ? ite.value : ite))
    }
    data.category = isObject(category) ? category.value : null
    data.shipment = isObject(shipment) ? shipment.value : null
    data.fundCode = isObject(fundCode) ? fundCode.value : null
    data.commodityClassification = isObject(commodityClassification)
      ? commodityClassification.value
      : null
    data.currency = isObject(currency) ? currency.value : null
    data.batchNumber = batch_number
    data.receiptNumber = receiptNumber
    data.waybillNumber = waybillNumber ? waybillNumber : null
    onAdvancedSearchSubmit(data)
  }

  // clear search when clear button clicked
  const onClearHandler = () => {
    dateFlatpickerfrom.current.flatpickr.clear()
    dateFlatpickerto.current.flatpickr.clear()
    setItem(null)
    setCategory(null)
    setShipment(null)
    setFundCode(null)
    setCommodityClassification(null)
    setCurrency(null)
    setBatchNumber("")
    setReceiptNumber("")
    setWaybillNumber("")
    setDate({ from: null, to: null })
    onAdvancedSearchSubmit({})
  }

  const onCommodityClassificationChangeHandler = value => {
    setCommodityClassification(value)
    setCategory(null)
    setItem(null)
    dispatch(providerDashboardAction.resetProviderItems())
    dispatch(getProviderItemCategories(value?.value ?? null))
  }

  // store Item Categories and fetch items based item category
  const onCategoryChangeHandler = cate => {
    setCategory(cate)
    dispatch(getProviderItems(cate.value))
  }

  // store multiple items value selected
  const onItemChangeHandler = item => {
    const check = item.some(ite => ite.value === "all")
    if (check) {
      setItem(filteredItems)
    } else {
      setItem(item)
    }
  }

  // store batch number value
  const onBatchNumberChange = batch_number => {
    setBatchNumber(batch_number.target.value)
  }

  return (
    <Form
      onSubmit={e => {
        e.preventDefault()
        handleSubmit()
        return false
      }}
    >
      <div className="mb-3">
        <Row>
          <Col>
            <Label className="form-label">Item Categories</Label>
            {categories?.data && (
              <Select
                options={categories?.data}
                value={category}
                onChange={onCategoryChangeHandler}
              />
            )}
          </Col>
          <Col>
            <Label className="form-label">Items</Label>
            {filteredItems && (
              <Select
                isMulti={true}
                options={[{ value: "all", label: "All" }, ...filteredItems]}
                value={item}
                onChange={onItemChangeHandler}
              />
            )}
          </Col>
        </Row>
      </div>

      <div className="mb-3">
        <Row>
          <Col>
            <Label className="form-label">Currency</Label>
            {currencies?.data && (
              <Select
                options={currencies?.data}
                value={currency}
                onChange={value => setCurrency(value)}
              />
            )}
          </Col>
          <Col>
            <Label className="form-label">Batch Number</Label>
            <Input
              name="batch_number"
              placeholder="Batch Number"
              type="text"
              onChange={onBatchNumberChange}
              value={batch_number}
            />
          </Col>
        </Row>
      </div>

      <div className="mb-3">
        <Row>
          <Col>
            <Label className="form-label">Shipment</Label>
            {shipments?.data && (
              <Select
                options={shipments?.data}
                value={shipment}
                onChange={value => setShipment(value)}
              />
            )}
          </Col>
          <Col>
            <Label className="form-label">Fund Code</Label>
            {fundCodes?.data && (
              <Select
                options={fundCodes?.data}
                value={fundCode}
                onChange={value => setFundCode(value)}
              />
            )}
          </Col>
        </Row>
      </div>

      <div className="mb-3">
        <Row>
          <Col>
            <Label className="form-label">Unit Classification</Label>
            {commodityClassifications?.data && (
              <Select
                options={commodityClassifications?.data}
                value={commodityClassification}
                onChange={onCommodityClassificationChangeHandler}
              />
            )}
          </Col>
        </Row>
      </div>

      <div className="mb-3">
        <Row>
          <Col>
            <Label className="form-label">DS Number</Label>
            <Input
              name="receipt_number"
              placeholder="DS Number"
              type="text"
              onChange={e => setReceiptNumber(e.target.value)}
              value={receiptNumber}
            />
          </Col>
          <Col>
            <Label className="form-label">Waybill Number</Label>
            <Input
              name="waybill_number"
              placeholder="Waybill Number"
              type="text"
              onChange={e => setWaybillNumber(e.target.value)}
              value={waybillNumber}
            />
          </Col>
        </Row>
      </div>

      <div className="mb-3 ">
        <Row>
          <Col>
            <Label className="form-label">From Date</Label>
            <Flatpickr
              ref={dateFlatpickerfrom}
              className="form-control d-block"
              placeholder="dd M,yyyy"
              options={{
                altInput: true,
                altFormat: "Y-m-d",
                dateFormat: "Y-m-d",
              }}
              onChange={value => {
                setDate(prev => {
                  return {
                    ...prev,
                    from: moment(value[0]).format("YYYY-MM-DD"),
                  }
                })
              }}
            />
          </Col>
          <Col>
            <Label className="form-label">To Date</Label>
            <Flatpickr
              ref={dateFlatpickerto}
              className="form-control d-block"
              placeholder="dd M,yyyy"
              options={{
                altInput: true,
                altFormat: "Y-m-d",
                dateFormat: "Y-m-d",
              }}
              onChange={value => {
                setDate(prev => {
                  return { ...prev, to: moment(value[0]).format("YYYY-MM-DD") }
                })
              }}
            />
          </Col>
        </Row>
      </div>

      <ReportColumnSelector
        columns={columns}
        onToggle={onToggleColumn}
        onReorder={onReorder}
      />

      <div className="d-flex flex-wrap gap-2 mb-4">
        <Button
          type="submit"
          className="btn btn-sm btn-primary"
          color="primary"
          disabled={loading}
        >
          {loading ? "Please Wait..." : "Search"}
        </Button>
        <Button
          onClick={onClearHandler}
          className="btn btn-sm btn-danger"
          color="primary"
        >
          Clear
        </Button>
      </div>
    </Form>
  )
}

export default ProviderReceiptReportFilter
